<template>
  <div>
    <p class="mt-2">
      {{ t('warehouse.pages.settings.messages.enableToSelectAndOrderDetailsInAppointmentTile') }}
    </p>

    <v-switch v-model="enabled" color="secondary" density="compact">
      <template v-slot:label>
        <div :class="{ 'text--disabled': !enabled }">
          <div class="pl-1">
            <span>
              {{
                enabled
                  ? t('warehouse.pages.settings.messages.enabledShowingCustomGrid')
                  : t('warehouse.pages.settings.messages.disabledShowingDefaultGrid')
              }}
            </span>
          </div>
        </div>
      </template>
    </v-switch>

    <p :class="{ 'text--disabled': !enabled }">
      {{
        t(
          'warehouse.pages.settings.messages.selectAndOrderDetailsYouWantToDisplayInEachAppointmentTile'
        )
      }}:
    </p>

    <v-alert border="start" type="info" v-if="enabled && lastItem">
      {{ t('warehouse.pages.settings.messages.youMustKeepAtLeastOneItemSelected') }}
    </v-alert>

    <!-- ACTIVE ITEMS: -->
    <div id="datablock-list">
      <v-checkbox
        v-for="key in config"
        v-model="config"
        :key="key"
        :value="key"
        hide-details
        :disabled="!enabled || lastItem">
        <template v-slot:prepend>
          <v-icon class="handle">mdi-arrow-split-horizontal</v-icon>
        </template>
        <template v-slot:label>
          <grid-tile-data-block-checkbox-label :item="dataBlocks[key]" :disabled="!enabled" />
        </template>
      </v-checkbox>
    </div>

    <!-- INACTIVE ITEMS: -->
    <div>
      <v-checkbox
        v-for="(item, key) in dataBlocks"
        v-model="config"
        :key="key"
        :value="key"
        v-show="!config.includes(key)"
        hide-details
        :disabled="!enabled">
        <template v-slot:prepend>
          <v-icon class="handle" disabled>mdi-arrow-split-horizontal</v-icon>
        </template>
        <template v-slot:label>
          <grid-tile-data-block-checkbox-label :item="item" :disabled="!enabled" />
        </template>
      </v-checkbox>
    </div>

    <p class="mt-8 font-size-small">
      <span class="font-weight-bold">{{ t('global.basicTerms.notes') }}:</span>
      {{ t('warehouse.pages.settings.messages.changesWillBeAppliedToPastAndFutureAppointments') }}
    </p>
  </div>
</template>

<script>
import { computed } from 'vue';
import Sortable from 'sortablejs';
import { cloneDeep, isArray, isString } from 'lodash';
import {
  buildGridTileLoadTypeCustomFormFieldKey,
  CustomFieldType,
  CustomFormsFeatures,
  isGridTileLoadTypeCustomFormFieldKey,
  t
} from '@nova/core';
import customFormsService from '@satellite/services/custom-forms-service';
import useLimiter from '@satellite/composables/useLimiter';

export default {
  name: 'GridTileConfigEditor',
  setup(props) {
    const warehouseSettings = computed(() => props.warehouse?.settings);
    const { shouldShowHandlingUnits, handlingUnitsLabel } = useLimiter(warehouseSettings);

    return {
      shouldShowHandlingUnits,
      handlingUnitsLabel
    };
  },
  props: {
    inputEnabled: {
      type: Boolean,
      required: true
    },
    inputConfig: {
      type: Array,
      required: true
    },
    warehouse: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      enabled: false,
      config: [],
      dataBlocks: {
        company_name: {
          title: this.t('warehouse.pages.settings.labels.carrierCompanyName')
        },
        start_time: {
          title: this.t('warehouse.pages.settings.labels.startTime')
        },
        tags: {
          title: this.t('warehouse.pages.settings.labels.tags')
        },
        ref_number: {
          title: this.t('warehouse.pages.settings.labels.referenceNumber')
        },
        conf_number: {
          title: this.t('warehouse.pages.settings.labels.confirmationNumber')
        },
        dock_name: {
          title: this.t('warehouse.pages.settings.labels.dockName'),
          note: this.t('warehouse.pages.settings.messages.onlyAvailableForWeekAndMonthViews')
        },
        load_type: {
          title: this.t('warehouse.pages.settings.labels.loadTypeName')
        }
      }
    };
  },
  mounted() {
    // Add handling units if feature flag and settings are enabled
    if (this.shouldShowHandlingUnits) {
      this.dataBlocks['handling_units'] = {
        title: this.handlingUnitsLabel
      };
    }

    this.enabled = this.inputEnabled;

    const config = cloneDeep(this.inputConfig);

    const fieldsTmplt = this.warehouse?.customApptFieldsTemplate;

    if (isArray(fieldsTmplt)) {
      for (const field of fieldsTmplt) {
        // TODO: For now, we don't allow Doc/MultiDoc custom field types.
        if (field.type !== CustomFieldType.Document && field.type !== CustomFieldType.MultiDocument)
          this.dataBlocks['custom:' + field.label] = {
            title: field.label,
            note: this.t('global.specialtyTerms.customFields')
          };
      }
    }

    // Sync `mounted`: load load-type options first; only drop unknown `ltcf:` keys after a successful load.
    const filterConfig = loadTypeOptionsLoadedOk =>
      config.filter(item => {
        if (isString(item) && item.startsWith('custom:')) {
          return item in this.dataBlocks;
        }
        if (isString(item) && isGridTileLoadTypeCustomFormFieldKey(item)) {
          return loadTypeOptionsLoadedOk ? item in this.dataBlocks : true;
        }
        return true;
      });

    this.loadLoadTypeFormGridTileOptions()
      .then(() => {
        this.config = filterConfig(true);
      })
      .catch(() => {
        this.config = filterConfig(false);
      })
      .finally(() => {
        this.$nextTick(() => this.initSortable());
      });
  },
  computed: {
    lastItem: function () {
      return this.config.length === 1;
    }
  },
  methods: {
    t,
    async loadLoadTypeFormGridTileOptions() {
      const docks = this.warehouse?.docks || [];
      const loadTypeIds = [...new Set(docks.flatMap(d => d.loadTypeIds || []).filter(Boolean))];
      if (!loadTypeIds.length) {
        return;
      }

      // Covers API/network failures from getLoadTypesTriggers. Optional chaining when walking
      // trigger.flow keeps unexpected payload shapes from throwing (defensive, not a substitute for typing).
      try {
        const triggers = await customFormsService.getLoadTypesTriggers(loadTypeIds, {
          feature: CustomFormsFeatures.BookLoadType,
          isActive: true
        });

        const seen = new Set();
        for (const trigger of triggers || []) {
          const formFields = trigger?.flow?.formFrom?.formFields ?? [];
          for (const formField of formFields) {
            const id = formField.id;
            if (!id || seen.has(id)) {
              continue;
            }
            const fieldType = formField.field?.type;
            if (
              fieldType === CustomFieldType.Document ||
              fieldType === CustomFieldType.MultiDocument
            ) {
              continue;
            }
            seen.add(id);
            const key = buildGridTileLoadTypeCustomFormFieldKey(id);
            this.dataBlocks[key] = {
              title: formField.overrideLabel || formField.field?.label || '',
              note: this.t('warehouse.pages.settings.messages.loadTypeCustomFormFieldGridTileNote')
            };
          }
        }
      } catch (error) {
        console.error(
          'GridTileConfigEditor: failed to load load-type form grid tile options',
          error
        );
      }
    },
    initSortable() {
      const _this = this;
      const container = document.querySelector('#datablock-list');
      if (!container) {
        return;
      }

      Sortable.create(container, {
        handle: '.handle',
        animation: 150,
        filter: '.filtered',
        ghostClass: 'ghost-item',
        onEnd({ oldIndex, newIndex }) {
          const movedItem = _this.config.splice(oldIndex, 1)[0];
          _this.config.splice(newIndex, 0, movedItem);
          _this.config = [..._this.config];
        }
      });
    }
  },
  watch: {
    config: {
      handler(val) {
        this.$emit('configUpdated', cloneDeep(val));
      },
      deep: true
    },
    enabled: function (val) {
      if (val) {
        // If the user just switched on the new system, and nothing is selected in the config,
        // then just go ahead and select everything. This way they start out with "all the things".
        if (this.config.length === 0) {
          for (const key of Object.keys(this.dataBlocks)) {
            this.config.push(key);
          }
        }
      }
      this.$emit('enabledUpdated', cloneDeep(val));
    }
  }
};
</script>
